import initializeComponent from 'Shared/react-initialize';
import { cn } from '@ifixit/ui/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@ifixit/ui/components/tooltip';
import { useBreakpoint } from '@ifixit/ui/hooks/use-media-query';
import { FlagBadge, FlagBadgeStyle } from './FlagBadge';
import { _js } from '@ifixit/utils/localize';
import { Dispatch, useMemo, useState } from 'react';
import { FlexScrollGradient } from './FlexScrollGradient';
import { hiddenWrapContainerClasses, useHiddenWrap } from '@ifixit/breadcrumbs/FlexHiddenWrap';
import { GuideData } from './GuideHeader';
import { useHistoryState } from './HistoryState';
import { DifficultyBadge } from './DifficultyBadge';
import { TimeTakenBadge } from './TimeTakenBadge';

const KNOWN_ICONS = new Set([
   'wrench',
   'hospital',
   'pen-line',
   'user',
   'circle-star',
   'badge-check',
   'graduation-cap',
   'octagon-exclamation',
   'images',
   'highlighter-line',
   'camera',
   'code',
   'list-ul',
   'scissors',
   'file-circle-question',
   'file-circle-xmark',
   'image-slash',
   'face-clouds',
   'hammer-crash',
   'camera-viewfinder',
   'files',
   'trash-clock',
   'pen-field',
   'split',
   'mountain',
   'box-archive',
   'route',
   'file-circle-exclamation',
   'message-captions',
   'handshake',
   'robot',
]);

const SOLID_ICONS = new Set(['badge-check', 'robot']);

export function getIcon(iconName: string): string {
   return KNOWN_ICONS.has(iconName) ? iconName : 'notdef';
}

export function getIconStyle(iconName: string): 'solid' | 'regular' {
   return SOLID_ICONS.has(iconName) ? 'solid' : 'regular';
}

const FlagBadgeStyleMapping: {
   [key: string]: FlagBadgeStyle;
} = {
   GUIDE_STARRED: {
      badgeClass: 'bg-brand-50 border-blue-300 text-blue-700',
      iconClass: 'text-blue-500',
   },
   GUIDE_OEM: {
      badgeClass: 'bg-green-100 border-green-300 text-green-700',
      iconClass: 'text-green-500',
   },
   MANUFACTURER_PROVIDED: {
      badgeClass: 'bg-green-100 border-green-300 text-green-700',
      iconClass: 'text-green-500',
   },
};

const FlagBadgePriorityMapping: {
   [key: string]: number;
} = {
   GUIDE_STARRED: 3,
   GUIDE_OEM: 2,
   MANUFACTURER_PROVIDED: 1,
};

export const DefaultFlagStyle: FlagBadgeStyle = {
   badgeClass: 'bg-white border-amber-300 text-amber-700',
   iconClass: 'text-amber-500',
};

const MoreButtonReservedBuffer = 100;
const MoreButtonGap = 10;
const ItemGap = 6;

export type FlagSectionProps = Pick<
   GuideData,
   'difficulty' | 'difficultyName' | 'timeRequired' | 'flags' | 'isTeardown'
>;
export function FlagSection({
   difficulty,
   timeRequired,
   flags,
   isTeardown,
   difficultyName,
}: FlagSectionProps) {
   const isAboveSm = useBreakpoint('sm');
   const [isExpanded, setIsExpanded] = useHistoryState(false, 'flagSectionExpanded');
   const isMobile = isAboveSm === false;
   const nonFullWidthFlags = flags.filter(flag => !flag.fullWidth);

   const hasContent = !isTeardown || flags.length > 0;

   if (!hasContent) {
      return null;
   }

   if (isMobile) {
      return (
         <FlexScrollGradient className="pt-3 pb-4 overflow-x-hidden">
            <div
               className={cn(
                  'flex items-center gap-x-[6px] flags-list',
                  isTeardown && 'sm:justify-center'
               )}
            >
               <StaticFlagSection
                  difficultyName={difficultyName}
                  isTeardown={isTeardown}
                  difficulty={difficulty}
                  timeRequired={timeRequired}
                  flags={nonFullWidthFlags}
               />
            </div>
         </FlexScrollGradient>
      );
   }

   return (
      <DynamicFlagSection
         difficultyName={difficultyName}
         difficulty={difficulty}
         timeRequired={timeRequired}
         flags={nonFullWidthFlags}
         isTeardown={isTeardown}
         isExpanded={isExpanded}
         setIsExpanded={setIsExpanded}
      />
   );
}

function DynamicFlagSection({
   difficulty,
   difficultyName,
   timeRequired,
   flags,
   isExpanded,
   isTeardown,
   setIsExpanded,
}: FlagSectionProps & {
   isExpanded: boolean;
   setIsExpanded: Dispatch<boolean>;
}) {
   const [hiddenFlagCount, setHiddenFlagCount] = useState<number>(0);
   const [left, setLeft] = useState<number>();

   const onDesktopFlagWrap = useMemo(
      () => (children: HTMLElement[]) => setHiddenFlagCount(() => children.length),
      []
   );

   const onDesktopFlagShown = (children: HTMLElement[], container: HTMLElement) => {
      // Batch all width reads together to minimize reflows
      const containerWidth = container.offsetWidth;
      let childrenWidthsMinusGap = 0;
      for (const child of children) {
         childrenWidthsMinusGap += child.offsetWidth;
      }
      const childrenWidth = ItemGap * (children.length - 1) + childrenWidthsMinusGap;
      let offset = containerWidth - childrenWidth;
      if (isTeardown) {
         offset = offset / 2;
      }
      setLeft(-offset + MoreButtonGap);
   };

   const showLink = !isExpanded && hiddenFlagCount > 0;

   const { ref: hiddenWrapRef } = useHiddenWrap<HTMLDivElement>({
      wrappedChildrenEffect: onDesktopFlagWrap,
      shownChildrenEffect: onDesktopFlagShown,
   });

   const flagSection = (
      <StaticFlagSection
         difficulty={difficulty}
         difficultyName={difficultyName}
         timeRequired={timeRequired}
         flags={flags}
         isTeardown={isTeardown}
      />
   );

   const justifyClass = isTeardown ? 'sm:justify-center' : '';

   return (
      <div className={cn('flex pt-3 pb-4 items-center', justifyClass)}>
         {isExpanded ? (
            <div
               ref={hiddenWrapRef}
               className={cn(
                  hiddenWrapContainerClasses,
                  'gap-[6px] max-w-full flags-list',
                  justifyClass
               )}
            >
               {flagSection}
            </div>
         ) : (
            <div
               ref={hiddenWrapRef}
               className={cn(
                  hiddenWrapContainerClasses,
                  'items-center gap-x-[6px] flags-list h-7 sm:h-[30px]',
                  justifyClass
               )}
               style={{ maxWidth: `calc(100% - ${MoreButtonReservedBuffer}px)` }}
            >
               {flagSection}
            </div>
         )}
         {showLink && (
            <button
               type="button"
               className={cn(
                  'relative text-sm bg-transparent border-none cursor-pointer whitespace-nowrap hover:no-underline',
                  isTeardown
                     ? 'text-white hover:text-gray-200'
                     : 'text-gray-700 hover:text-gray-900'
               )}
               style={{ left: `${left}px` }}
               onClick={() => setIsExpanded(!isExpanded)}
            >
               {_js('+%1 more', hiddenFlagCount.toString())}
            </button>
         )}
      </div>
   );
}

function StaticFlagSection({
   difficulty,
   difficultyName,
   timeRequired,
   flags,
   isTeardown,
}: FlagSectionProps) {
   const sortedFlags = flags.sort(
      (a, b) =>
         (FlagBadgePriorityMapping[b.flagid] || -1) - (FlagBadgePriorityMapping[a.flagid] || -1)
   );
   const Flags = sortedFlags.map(flag => {
      const badgeStyles = FlagBadgeStyleMapping[flag.flagid] || DefaultFlagStyle;
      const badgeProps =
         'image' in flag
            ? { imageSrc: flag.image }
            : { icon: getIcon(flag.icon), iconStyle: getIconStyle(flag.icon) };
      return (
         <div key={flag.title} className="flex flag">
            <Tooltip>
               <TooltipTrigger asChild>
                  <FlagBadge {...badgeStyles} {...badgeProps} className="flagTitle">
                     {flag.title}
                  </FlagBadge>
               </TooltipTrigger>
               <TooltipContent className="text-sm flagText">{flag.text}</TooltipContent>
            </Tooltip>
         </div>
      );
   });

   if (isTeardown) {
      return <>{Flags}</>;
   }

   return (
      <>
         <div className="flex">
            <TimeTakenBadge timeRequired={timeRequired} className="guide-time-required" />
         </div>
         <div className="flex">
            <DifficultyBadge
               difficulty={difficulty}
               difficultyName={difficultyName}
               className={`guide-difficulty guide-difficulty-${difficulty}`}
            />
         </div>
         {Flags}
      </>
   );
}

initializeComponent('FlagSectionComponent', FlagSection);
